<!--@format-->
<template>
    <div class="landing-page-template-widget">
        <loading-card :is-loading="isLoading" />
        <div class="widget-header">
            <h3>{{ title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div class="widget-container">
                <starting-step
                    v-if="currentStep === 'startingPoint'"
                    :template-options="templateOptions"
                    :selected-template-option="selectedTemplateOption"
                    @input="handleInputTemplateOption"
                />
                <existing-element-step
                    v-if="isFromExistingElementStep"
                    :type="elementType"
                    :form="form"
                />
                <settings-step
                    v-if="currentStep === 'settings'"
                    :account-id="accountId"
                    :form="form"
                    :v="$v"
                />
            </div>
        </div>
        <div class="widget-footer">
            <div class="actions-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="actions-container">
                <app-button
                    v-if="hasBackAction && !isEditMode"
                    class="secondary-btn"
                    @click="goBack"
                >
                    <span class="fa fa-arrow-left"></span>
                    {{ backLabel }}
                </app-button>
                <app-button
                    :disabled="
                        (currentStep === 'settings' || isFromExistingElementStep) && !isFormValid
                    "
                    @click="goNext"
                >
                    {{ nextLabel }}
                    <span class="fa fa-arrow-right"></span>
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus';
import { LandingPageTemplateService } from '@/scripts/services/landing-page/landing-page-template-service';
import { required } from 'vuelidate/lib/validators';

const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const StartingStep = () =>
    import('@/components/sliding-widgets/landing-page-template-widget/starting-step.vue');
const ExistingElementStep = () =>
    import('@/components/sliding-widgets/landing-page-template-widget/existing-element-step.vue');
const SettingsStep = () =>
    import('@/components/sliding-widgets/landing-page-template-widget/settings-step.vue');
const LoadingCard = () => import('@/components/common/loading-card');
import BrandingMixin from '@/mixins/branding-mixin';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'landing-page-template-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'starting-step': StartingStep,
        'existing-element-step': ExistingElementStep,
        'loading-card': LoadingCard,
        'settings-step': SettingsStep,
    },

    mixins: [BrandingMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,

            selectedTemplateOption: null,

            lastActiveStep: null,

            defaultForm: {
                landingPageTemplateName: null,
                metaTitle: null,
                favIconUrl: null,
                isPublished: false,
                config: null,
                categories: [],
                hasBranding: null,
                modifiedBy: {
                    name: null,
                    profileImageUrl: null,
                },

                updatedDate: null,

                createdDate: null,

                publishedDate: null,
            },

            form: {
                landingPageTemplateName: null,
                metaTitle: null,
                favIconUrl: null,
                config: null,
                isPublished: false,
                categories: [],
                hasBranding: null,
                modifiedBy: {
                    name: null,
                    profileImageUrl: null,
                },

                updatedDate: null,

                createdDate: null,

                publishedDate: null,
            },

            steps: {
                startingPoint: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep(this.selectedTemplateOption.value);
                            },
                        },
                    },
                },

                existingPage: {
                    title: 'Select an Existing Page',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep('settings');
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                existingTemplate: {
                    title: 'Select a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep('settings');
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                settings: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.isEditMode
                                    ? this.patchLandingPageTemplate()
                                    : this.createLandingPageTemplate();
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep(this.lastActiveStep);
                            },
                        },
                    },
                },
            },

            currentStep: 'startingPoint',
        };
    },

    validations: {
        form: {
            landingPageTemplateName: {
                required,
            },
        },
    },

    computed: {
        title() {
            return this.steps[this.currentStep].title;
        },

        isFormValid() {
            return !this.$v.$invalid;
        },

        isClientPage() {
            return this.$store.state.context.client.id;
        },

        templateOptions() {
            const options = [
                {
                    imageUrl: require('@/assets/images/existing-template.png'),
                    name: 'Existing Template',
                    value: 'existingTemplate',
                },
                {
                    imageUrl: require('@/assets/images/start-fresh.png'),
                    name: 'Start Fresh',
                    value: 'settings',
                },
            ];

            if (this.isClientPage) {
                options.unshift({
                    imageUrl: require('@/assets/images/landing-page-previews.png'),
                    name: 'Existing Page',
                    value: 'existingPage',
                });
            }

            return options;
        },

        isFromExistingElementStep() {
            return this.currentStep === 'existingPage' || this.currentStep === 'existingTemplate';
        },

        elementType() {
            return this.currentStep === 'existingPage' ? 'landing-page' : 'landing-page-template';
        },

        hasBackAction() {
            return this.steps[this.currentStep].actions.backAction !== undefined;
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        backLabel() {
            return this.steps[this.currentStep].actions.backAction.label;
        },

        nextLabel() {
            return this.steps[this.currentStep].actions.nextAction.label;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        isEditMode() {
            return (
                this.widget.landingPageTemplateId !== null &&
                this.widget.landingPageTemplateId !== undefined
            );
        },

        isFromStudio() {
            return this.widget.options ? this.widget.options.isFromStudio : false;
        },
    },

    created() {
        if (this.isEditMode) {
            return this.getEditLandingPageTemplateData();
        }
        this.selectedTemplateOption = this.templateOptions[0];
        this.isLoading = false;
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.lastActiveStep = this.currentStep;
            this.currentStep = step;
        },

        handleInputTemplateOption(newTemplateSelected) {
            this.selectedTemplateOption = newTemplateSelected;
        },

        uploadFavicon() {
            return new Promise((resolve, reject) => {
                const base64RegExp = /data:image\/[^;]+;base64[^"]+/;
                if (this.form.favIconUrl && this.form.favIconUrl.match(base64RegExp)) {
                    const file = {
                        name: `${this.form.landingPageTemplateName
                            .toLowerCase()
                            .replace(' ', '-')}-lp-favicon`,

                        url: this.form.favIconUrl,
                    };
                    this.$api
                        .post('/api/Files', file)
                        .then((response) => {
                            this.form.favIconUrl = response.data;
                            resolve();
                        })
                        .catch(reject);
                    resolve();
                } else {
                    resolve();
                }
            });
        },

        submitLandingPageTemplate() {
            return new Promise((resolve, reject) => {
                const payload = {
                    Name: this.form.landingPageTemplateName,
                    MetaTitle: this.form.metaTitle,
                    FaviconUrl: this.form.favIconUrl,
                    TemplateJson: this.form.config,
                    Categories: this.form.categories,
                    AccountId: this.accountId,
                    HasBranding: this.hasLandingPageBranding(JSON.parse(this.form.config)),
                };

                LandingPageTemplateService.post(payload)
                    .then(({ data: { Id } }) => {
                        this.$helpers.showSuccessNotification(
                            'Landing page template created successfully.',
                        );
                        resolve(Id);
                    })
                    .catch(reject);
            });
        },

        createLandingPageTemplate() {
            this.isLoading = true;
            this.uploadFavicon()
                .then(this.submitLandingPageTemplate)
                .then((Id) => {
                    this.closeWidget();
                    this.$router.push(`/LandingPages/Templates/${Id}/Design`);
                })
                .catch((error) => {
                    const message = error.response.data.Message;

                    if (message.includes('name already exists')) {
                        this.$helpers.showFailNotification(
                            'A Landing Page Template with the same name already exists.',
                            { level: LogLevel.INFO },
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred trying to create the landing page.',
                            { error },
                        );
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Edit
        getEditLandingPageTemplateData() {
            return new Promise((resolve, reject) => {
                const { landingPageTemplateId } = this.widget;
                const config = {
                    expand: 'Categories($select=Name),UpdatedByUser($select=FirstName,LastName,ProfileImageUrl)',
                };

                LandingPageTemplateService.get(landingPageTemplateId, config)
                    .then((response) => {
                        this.setupEditLandingPageTemplateInformation(response);
                        this.goToStep('settings');
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get landing page info', {
                            error,
                        });
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        setupEditLandingPageTemplateInformation({ data }) {
            const [item] = data.value;
            this.form = {
                ...this.form,
                landingPageTemplateName: item.Name,
                metaTitle: item.MetaTitle,
                favIconUrl: item.FaviconUrl,
                categories: item.Categories.map(({ Name }) => Name),
                createdDate: this.$moment(item.CreatedAt),
                publishedDate: this.$moment(item.PublishedAt),
                updatedDate: this.$moment(item.UpdatedAt),
                hasBrandig: item.hasBrandig,
                modifiedBy: item.UpdatedByUser
                    ? {
                          name: `${item.UpdatedByUser.FirstName} ${item.UpdatedByUser.LastName}`,
                          profileImageUrl: item.UpdatedByUser.ProfileImageUrl,
                      }
                    : null,
            };
        },

        patchLandingPageTemplate() {
            this.isLoading = true;

            const { landingPageTemplateId } = this.widget;

            const changes = [
                {
                    name: 'Name',
                    value: this.form.landingPageTemplateName,
                },
                {
                    name: 'MetaTitle',
                    value: this.form.metaTitle,
                },
                {
                    name: 'FaviconUrl',
                    value: this.form.favIconUrl,
                },
                {
                    name: 'Categories',
                    value: this.form.categories,
                },
            ];

            LandingPageTemplateService.patch(landingPageTemplateId, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Landing page template updated successfully.',
                    );
                    EventBus.$emit('landing-page-template:update');
                    this.closeWidget();

                    const dest =
                        `/LandingPages/Templates/${landingPageTemplateId}/Design` +
                        `${this.isFromStudio ? '?fromStudio=true' : ''}`;

                    this.$router.push(dest);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'Failed to update the landing page template.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.landing-page-template-widget .widget-footer .transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.landing-page-template-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.landing-page-template-widget .widget-footer .actions-container {
    display: flex;
}

.landing-page-template-widget .widget-footer .secondary-btn {
    background: transparent;
    border: 1px solid#18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.landing-page-template-widget .widget-footer .secondary-btn:hover {
    background: #dbedfa;
}

.landing-page-template-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.landing-page-template-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}
</style>
